/**
 * TanStack Query hooks.
 *
 * §5.1 requires one place for query keys, caching, invalidation and retry
 * policy. Every server interaction in the app goes through a hook here, so the
 * cache can never be invalidated inconsistently from two different pages.
 */
import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { ApiError, ErrorCode, api, newIdempotencyKey, qs, request } from './api'
import type {
  AdminHealth, AffiliateOffer, DashboardSummary, DeadLetter, Delivery, Domain,
  DomainDetail, DomainEvent, EntitlementSet, FieldSpec, NotificationChannel,
  Page, ReleaseAnalytics, Rule, RuleDocument, RuleMatch, RuleRun, RuleValidation, TestResult,
  TldInfo, UsageSummary, User, Watcher,
} from './types'

/** Query keys are namespaced so a mutation can invalidate a whole subtree. */
export const keys = {
  me: ['me'] as const,
  usage: ['usage'] as const,
  plans: ['plans'] as const,
  meta: {
    ruleFields: ['meta', 'rule-fields'] as const,
    tlds: ['meta', 'tlds'] as const,
    tldCounts: ['meta', 'tld-counts'] as const,
  },
  registrars: { all: ['registrars'] as const },
  registrarProviders: ['admin', 'registrar-providers'] as const,
  registration: { budget: ['registration', 'budget'] as const, orders: (s?: string) => ['registration', 'orders', s ?? ''] as const },
  domains: {
    all: ['domains'] as const,
    list: (params: Record<string, unknown>) => ['domains', 'list', params] as const,
    detail: (id: string) => ['domains', 'detail', id] as const,
    events: (id: string) => ['domains', 'events', id] as const,
    score: (id: string) => ['domains', 'score', id] as const,
    registrars: (id: string) => ['domains', 'registrars', id] as const,
  },
  rules: {
    all: ['rules'] as const,
    list: (status?: string) => ['rules', 'list', status ?? ''] as const,
    detail: (id: string) => ['rules', 'detail', id] as const,
    runs: (id: string) => ['rules', 'runs', id] as const,
    matches: (id: string, status: string) => ['rules', 'matches', id, status] as const,
  },
  watchers: (status?: string) => ['watchers', status ?? ''] as const,
  events: (params: Record<string, unknown>) => ['events', params] as const,
  releaseAnalytics: (params: Record<string, unknown>) => ['release-analytics', params] as const,
  channels: ['channels'] as const,
  deliveries: (params: Record<string, unknown>) => ['deliveries', params] as const,
  dashboard: (days: number) => ['dashboard', days] as const,
  admin: {
    health: ['admin', 'health'] as const,
    deadLetters: (queue: string) => ['admin', 'dead-letters', queue] as const,
    dataSources: ['admin', 'data-sources'] as const,
    resolverLimits: ['admin', 'resolver-limits'] as const,
  },
}

/**
 * Retry policy: never retry a client error, because a 400/403/404 will fail
 * identically on the second attempt and just delays the message the user needs.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (!error.retryable) return false
    if (error.code === ErrorCode.RateLimited) return failureCount < 1
  }
  return failureCount < 2
}

// ---- Account ----------------------------------------------------------------

export interface MeResponse {
  user: User
  entitlements: EntitlementSet
  usage: UsageSummary
  enforcement: {
    stage: string
    block_force_resolve: boolean
    block_export: boolean
    block_api: boolean
    suspended: boolean
    message?: string
    appealable: boolean
  }
}

export function useMe(options?: Partial<UseQueryOptions<MeResponse, ApiError>>) {
  return useQuery<MeResponse, ApiError>({
    queryKey: keys.me,
    queryFn: () => api.get<MeResponse>('/me'),
    staleTime: 60_000,
    retry: shouldRetry,
    ...options,
  })
}

export function useUsage() {
  return useQuery<UsageSummary, ApiError>({
    queryKey: keys.usage,
    queryFn: () => api.get<UsageSummary>('/me/usage'),
    staleTime: 30_000,
    retry: shouldRetry,
  })
}

export function usePlans() {
  return useQuery({
    queryKey: keys.plans,
    queryFn: () => api.get<{ plans: unknown[]; payments_enabled: boolean; notice: string }>('/plans'),
    staleTime: 300_000,
  })
}

// ---- Metadata ---------------------------------------------------------------

export function useRuleFields() {
  return useQuery({
    queryKey: keys.meta.ruleFields,
    queryFn: () =>
      api.get<{ fields: FieldSpec[]; features: string[]; statuses: string[]; sorts: string[] }>(
        '/meta/rule-fields',
      ),
    // The field catalogue changes only with a deployment.
    staleTime: Infinity,
  })
}

export interface OllamaConfig {
  enabled: boolean
  provider?: 'ollama' | 'lmstudio'
  base_url: string
  model: string
  /** Write-only. The server never returns it; blank on submit means unchanged. */
  token?: string
  timeout_seconds: number
}

export interface AIProgress {
  eligible: number
  analysed: number
  failed: number
  pending: number
}

export interface AIActivity {
  domain: string
  reading?: string
  meaning?: string
  error?: string
  at: string
}

export interface AISweepCurrent {
  domain?: string
  started_at?: string
}

export function useAISettings() {
  return useQuery({
    queryKey: ['admin', 'ai-settings'] as const,
    queryFn: () =>
      api.get<{
        config: OllamaConfig
        progress: AIProgress
        prompt_id: string
        has_token: boolean
        current?: AISweepCurrent
        recent?: AIActivity[]
      }>('/admin/ai/settings'),
    // Progress moves as the sweep runs.
    refetchInterval: 3_000,
  })
}

export function useSetAISettings() {
  const qc = useQueryClient()
  return useMutation<{ config: OllamaConfig; has_token: boolean }, ApiError, OllamaConfig>({
    mutationFn: (body) => api.patch('/admin/ai/settings', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ai-settings'] }),
  })
}

export interface AITestResult {
  reachable: boolean
  analysed?: boolean
  error?: string
  label?: string
  reply?: unknown
}

export function useTestAIModel() {
  return useMutation<AITestResult, ApiError, OllamaConfig & { label?: string }>({
    mutationFn: (body) => api.post('/admin/ai/test', body),
  })
}

/** ---- 语义库（按域名主体，一份释义覆盖所有后缀）---- */

export interface LabelSemantics {
  label: string
  pinyin_syllables?: number
  pinyin_ambiguous: boolean
  initial_abbr: boolean
  lucky: boolean
  in_dictionary: boolean
  words?: string
  note?: string
  ai_reply?: unknown
  ai_model?: string
  ai_at?: string
  ai_error?: string
  ai_queued_at?: string
  manual_reading?: string
  manual_meaning?: string
  manual_note?: string
  /** null 表示从没手动编辑过，此时标签由规则给出。 */
  tags: string[] | null
  source: 'library' | 'manual'
  updated_at: string
  /** 服务端算好的最终结果，前端不再重复一遍优先级规则。 */
  effective_tags: string[]
  effective_reading?: string
  effective_meaning?: string
  reading_source?: 'manual' | 'ai' | 'rule'
  domain_count: number
}

export interface LabelQuery {
  q?: string
  tag?: string
  source?: string
  has_ai?: boolean
  manual?: boolean
  sort?: string
  order?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export function useLabels(query: LabelQuery) {
  const params = new URLSearchParams()
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) params.set(k, String(v))
  })
  return useQuery({
    queryKey: ['labels', params.toString()] as const,
    queryFn: () =>
      api.get<{ items: LabelSemantics[]; total: number; tags: string[] }>(
        `/labels?${params.toString()}`,
      ),
    placeholderData: (prev) => prev,
    // A queued row turns into a reading without anything else happening on the
    // page, so poll while this page actually contains queued work. Once the
    // worker clears ai_queued_at the polling stops; an idle semantics page must
    // not issue the same list query forever.
    refetchInterval: (result) =>
      result.state.data?.items.some((item) => Boolean(item.ai_queued_at)) ? 5_000 : false,
  })
}

function useLabelMutation<TBody>(
  fn: (label: string, body: TBody) => Promise<LabelSemantics | void>,
) {
  const qc = useQueryClient()
  return useMutation<LabelSemantics | void, ApiError, { label: string; body: TBody }>({
    mutationFn: ({ label, body }) => fn(label, body),
    // Both lists show the same readings, so both go stale together.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labels'] })
      qc.invalidateQueries({ queryKey: ['domains'] })
    },
  })
}

export function useCreateLabel() {
  const qc = useQueryClient()
  return useMutation<LabelSemantics, ApiError, string>({
    mutationFn: (label) => api.post('/admin/labels', { label }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['labels'] }),
  })
}

export interface LabelEdit {
  manual_reading?: string
  manual_meaning?: string
  manual_note?: string
  tags?: string[] | null
}

export function useUpdateLabel() {
  return useLabelMutation<LabelEdit>((label, body) =>
    api.patch(`/admin/labels/${encodeURIComponent(label)}`, body),
  )
}

export function useDeleteLabel() {
  return useLabelMutation<void>((label) =>
    api.delete(`/admin/labels/${encodeURIComponent(label)}`),
  )
}

export function useAnalyzeLabel() {
  return useLabelMutation<void>((label) =>
    api.post(`/admin/labels/${encodeURIComponent(label)}/analyze`, {}),
  )
}

/** ---- 标签规则：条件命中就赋标签 ---- */

export interface TagRule {
  id: string
  tag: string
  description: string
  rule_json: RuleDocument
  enabled: boolean
  priority: number
  match_count: number
  /** 存量规则在 schema 变动后可能无法解析；非空即表示这条规则当前不生效。 */
  compile_error?: string
  updated_at: string
}

export function useTagRules() {
  return useQuery({
    queryKey: ['tag-rules'] as const,
    queryFn: () => api.get<{ items: TagRule[]; pending: number }>('/tag-rules'),
    // A rule change restages every label; the counts move for minutes after.
    refetchInterval: 5_000,
  })
}

/** The tags currently in use, for filter menus. */
export function useTagVocabulary() {
  return useQuery({
    queryKey: ['labels', 'vocabulary'] as const,
    queryFn: () => api.get<{ tags: string[] }>('/labels?limit=1'),
    select: (d) => d.tags ?? [],
    staleTime: 60_000,
  })
}

/** The subset of rule fields a bare label can actually decide. */
export function useTagRuleFields() {
  return useQuery({
    queryKey: ['tag-rules', 'fields'] as const,
    queryFn: () => api.get<{ fields: FieldSpec[] }>('/tag-rules/fields'),
    staleTime: 60 * 60_000,
  })
}

export function useSaveTagRule() {
  const qc = useQueryClient()
  return useMutation<TagRule, ApiError, Partial<TagRule>>({
    mutationFn: (body) =>
      body.id
        ? api.patch(`/admin/tag-rules/${body.id}`, body)
        : api.post('/admin/tag-rules', body),
    // Saving a rule restages every label, so both tables go stale.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tag-rules'] })
      qc.invalidateQueries({ queryKey: ['labels'] })
      qc.invalidateQueries({ queryKey: ['domains'] })
    },
  })
}

export function useDeleteTagRule() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete(`/admin/tag-rules/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tag-rules'] })
      qc.invalidateQueries({ queryKey: ['labels'] })
    },
  })
}

/** What a rule would catch, before it is saved against 27k labels. */
export function usePreviewTagRule() {
  return useMutation<{ matched: number; examples?: string[] }, ApiError, RuleDocument>({
    mutationFn: (rule_json) => api.post('/admin/tag-rules/preview', { rule_json }),
  })
}

export interface TldCount {
  tld: string
  count: number
}

/** Suffixes present in the library, most common first, for the filter menu. */
export function useTldCounts() {
  return useQuery({
    queryKey: keys.meta.tldCounts,
    queryFn: () => api.get<{ tlds: TldCount[] }>('/meta/tld-counts'),
    staleTime: 5 * 60_000,
  })
}

export interface RegistrationBudget {
  user_id: string
  enabled: boolean
  currency: string
  daily_cap_minor: number
  per_order_cap_minor: number
  max_concurrent: number
  spent_today_minor: number
}

export interface RegistrationOrder {
  id: string
  domain_ascii: string
  /** 成交的那一路（服务商#账号）；未成交时为空。 */
  provider?: string
  tld: string
  years: number
  max_price_minor: number
  currency: string
  charged_minor?: number
  status: 'queued' | 'registering' | 'verifying' | 'succeeded' | 'failed' | 'cancelled'
  attempts: number
  max_attempts: number
  start_at?: string
  expire_at?: string
  last_attempt_at?: string
  registered_at?: string
  last_error?: string
  created_at: string
}

/** Budget plus whether the platform can spend at all. */
export function useRegistrationBudget() {
  return useQuery({
    queryKey: keys.registration.budget,
    queryFn: () => api.get<{ budget: RegistrationBudget; provider_ready: boolean }>('/registration/budget'),
    staleTime: 15_000,
  })
}

export function useSetRegistrationBudget() {
  const qc = useQueryClient()
  return useMutation<RegistrationBudget, ApiError, Partial<RegistrationBudget>>({
    mutationFn: (body) => api.patch('/registration/budget', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.registration.budget }),
  })
}

/** 一个注册单里，某一家注册商自己的那条抢注线。 */
export interface RegistrationTrack {
  order_id: string
  provider: string
  /** 这条线用的是该服务商的哪套凭据。 */
  account: string
  status: 'queued' | 'registering' | 'verifying' | 'succeeded' | 'failed' | 'stopped'
  attempts: number
  next_attempt_at?: string
  last_attempt_at?: string
  last_error?: string
  updated_at: string
}

export function useRegistrationOrders(status?: string) {
  return useQuery({
    queryKey: keys.registration.orders(status),
    queryFn: () =>
      api.get<{
        orders: RegistrationOrder[]
        /** 按注册单 id 分组的各家进度。 */
        tracks: Record<string, RegistrationTrack[]>
      }>(`/registration/orders${status ? `?status=${status}` : ''}`),
    // Orders move on their own while a drop is being fought over.
    refetchInterval: 5_000,
  })
}

export interface RegistrationOrderInput {
  domain_ascii: string
  domain_id?: string
  years?: number
  max_price_minor: number
  currency?: string
  start_at?: string
  expire_at?: string
  max_attempts?: number
}

export function useCreateRegistrationOrder() {
  const qc = useQueryClient()
  return useMutation<RegistrationOrder, ApiError, RegistrationOrderInput>({
    mutationFn: (body) => api.post('/registration/orders', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['registration', 'orders'] }),
  })
}

export function useCancelRegistrationOrder() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete(`/registration/orders/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['registration', 'orders'] }),
  })
}

/** ---- 注册商接口（多服务商接入 / 测试台）---- */

export type ProviderFieldKind = 'secret' | 'text' | 'json' | 'bool'

export interface ProviderField {
  key: string
  label: string
  hint?: string
  kind: ProviderFieldKind
  required: boolean
  /** 仅注册时必填（查询不需要），例如 GoDaddy 的联系人。 */
  required_for?: string
}

/** 一家注册商下的一套凭据。多套凭据＝多条独立抢注线。 */
export interface ProviderAccountView {
  id: string
  label?: string
  enabled: boolean
  priority: number
  /** 非密文字段的当前值。密钥永远不会回传，只有 filled 告诉你存没存。 */
  values: Record<string, string>
  filled: Record<string, boolean>
  ready_to_check: boolean
  ready_to_register: boolean
  missing?: string[]
}

export interface ProviderConfigView {
  enabled: boolean
  priority: number
  endpoint?: string
  accounts: ProviderAccountView[]
  /** 任意一个账号可用即为 true。 */
  ready_to_check: boolean
  ready_to_register: boolean
  updated_at?: string
}

export interface RegistrarProvider {
  id: string
  name: string
  website?: string
  docs_url?: string
  can_check: boolean
  can_register: boolean
  drop_catch: boolean
  default_endpoint: string
  sandbox_endpoint?: string
  notes?: string
  fields: ProviderField[]
  config: ProviderConfigView
  error?: string
}

export interface PremiumRule {
  /** 后缀（"xyz"、"co.uk"）或某个具体域名（"12347799.xyz"）。 */
  pattern: string
  premium: boolean
  note?: string
}

export interface RegistrarProvidersResponse {
  providers: RegistrarProvider[]
  premium_rules?: PremiumRule[]
  registration_enabled: boolean
  availability_enabled: boolean
  enabled_order: string[]
  registration_ready: boolean
}

export function useRegistrarProviders() {
  return useQuery({
    queryKey: keys.registrarProviders,
    queryFn: () => api.get<RegistrarProvidersResponse>('/admin/registrar-providers'),
    staleTime: 15_000,
  })
}

export interface ProviderPatch {
  enabled?: boolean
  priority?: number
  endpoint?: string
  /** values 写给哪个账号；留空＝该服务商最初那套凭据。 */
  account?: string
  /** 留空表示「不改动」；填 '-' 表示清空。 */
  values?: Record<string, string>
  add_account?: string
  remove_account?: string
  account_enabled?: boolean
  account_label?: string
  account_priority?: number
}

export function useSaveRegistrarProvider() {
  const qc = useQueryClient()
  return useMutation<RegistrarProvidersResponse, ApiError, { provider: string; patch: ProviderPatch }>({
    mutationFn: ({ provider, patch }) => api.patch(`/admin/registrar-providers/${provider}`, patch),
    onSuccess: (data) => {
      // The response is the whole refreshed document, so it is written straight
      // into the cache: refetching would race the save and can show the page
      // its own pre-save state.
      qc.setQueryData(keys.registrarProviders, data)
      qc.invalidateQueries({ queryKey: keys.registration.budget })
    },
  })
}

export function useSetRegistrarSwitches() {
  const qc = useQueryClient()
  return useMutation<RegistrarProvidersResponse, ApiError,
    { registration_enabled?: boolean; availability_enabled?: boolean }>({
    mutationFn: (body) => api.patch('/admin/registrar-providers', body),
    onSuccess: (data) => {
      qc.setQueryData(keys.registrarProviders, data)
      qc.invalidateQueries({ queryKey: keys.registration.budget })
    },
  })
}

export function useSetPremiumRules() {
  const qc = useQueryClient()
  return useMutation<RegistrarProvidersResponse, ApiError, PremiumRule[]>({
    // PUT, not PATCH: the dialog shows the whole list, so what it sends is the
    // whole intent. A merge would make deleting a rule impossible to express.
    mutationFn: (rules) => request('/admin/registrar-providers/premium-rules', {
      method: 'PUT', body: { rules },
    }),
    onSuccess: (data) => qc.setQueryData(keys.registrarProviders, data),
  })
}

/** ---- 用户管理（管理员）---- */

export interface AdminUser {
  id: string
  email: string
  display_name: string
  role: 'admin' | 'user'
  status: 'active' | 'pending' | 'suspended' | 'deleted'
  email_verified_at?: string
  plan_code?: string
  plan_name?: string
  plan_version_id?: string
  risk_score: number
  enforcement_stage?: string
  active_watchers: number
  active_sessions: number
  created_at: string
  last_login_at?: string
  updated_at: string
}

export interface PlanOption {
  version_id: string
  code: string
  name: string
  version: number
  is_current: boolean
}

export interface AdminUserFilters {
  search?: string
  role?: string
  status?: string
  limit?: number
  offset?: number
}

export function useAdminUsers(filters: AdminUserFilters) {
  return useQuery({
    queryKey: ['admin', 'users', filters] as const,
    queryFn: () =>
      api.get<{ users: AdminUser[]; total: number; plans: PlanOption[] }>(
        `/admin/users${qs({ ...filters })}`),
    staleTime: 10_000,
  })
}

export function useUpdateAdminUser() {
  const qc = useQueryClient()
  return useMutation<AdminUser, ApiError, {
    id: string
    patch: {
      role?: string; status?: string; plan_version_id?: string
      display_name?: string; email?: string
    }
  }>({
    mutationFn: ({ id, patch }) => api.patch(`/admin/users/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export interface NewAdminUser {
  email: string
  /** 留空 = 服务端生成一个强随机密码，并在响应里返回一次。 */
  password?: string
  display_name?: string
  role?: string
  status?: string
  plan_version_id?: string
}

export function useCreateAdminUser() {
  const qc = useQueryClient()
  return useMutation<
    { user: AdminUser; password?: string; note?: string },
    ApiError,
    NewAdminUser
  >({
    mutationFn: (body) => api.post('/admin/users', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useDeleteAdminUser() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean }, ApiError, string>({
    mutationFn: (id) => api.delete(`/admin/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useResetUserPassword() {
  const qc = useQueryClient()
  return useMutation<
    { ok: boolean; sessions_revoked: boolean; password?: string; note?: string },
    ApiError,
    { id: string; password?: string }
  >({
    mutationFn: ({ id, password }) => api.post(`/admin/users/${id}/password`, { password }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useRevokeUserSessions() {
  const qc = useQueryClient()
  return useMutation<{ sessions_revoked: number }, ApiError, string>({
    mutationFn: (id) => api.post(`/admin/users/${id}/revoke-sessions`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/** ---- 相似域名推荐（EPIC-25）---- */

export interface SimilarVariant {
  domain_ascii: string
  sld: string
  tld: string
  /** 产生这个变体的规则，展示用——每条建议都必须说得清是怎么来的。 */
  kind: string
  explanation: string
  distance: number
  /** 平台见过这个域名吗。false 时 status 为空，**不代表可注册**。 */
  known: boolean
  status?: string
  domain_id?: string
  status_confidence?: number
}

export function useSimilarDomains() {
  return useMutation<{
    seed: string
    variants: SimilarVariant[]
    kinds: Array<{ kind: string; label: string }>
    disclaimer: string
  }, ApiError, { domain: string; tlds?: string[]; kinds?: string[]; limit?: number }>({
    mutationFn: (body) => api.post('/domains/similar', body),
  })
}

/** ---- 域名历史与外部信号（EPIC-22）---- */

export interface DomainHistoryAggregate {
  domain_id: string
  /** 全部可空：未知不等于 0。没数据时字段直接不出现。 */
  registration_count?: number
  drop_count?: number
  total_registered_days?: number
  last_registration_duration_days?: number
  first_registered_at?: string
  last_registered_at?: string
  /** events = 由平台自己的事件推导，起点是开始监控的那天，是下界不是总数。 */
  method: string
  confidence: number
  computed_at: string
}

export interface EnrichmentSignal {
  kind: 'wayback' | 'gfw' | string
  source: string
  as_of: string
  confidence: number
  method_version: string
  payload: unknown
}

export interface WaybackPayload {
  capture_count: number
  first_capture_at?: string
  last_capture_at?: string
  status_summary?: Record<string, number>
  /** true 表示查询被截断，数字是下界。 */
  truncated: boolean
}

export function useDomainHistory(domainId: string | null) {
  return useQuery({
    queryKey: ['domains', 'history', domainId] as const,
    enabled: !!domainId,
    queryFn: () =>
      api.get<{
        history: DomainHistoryAggregate | null
        signals: EnrichmentSignal[]
        /** 哪些数据源在这个部署里开着——用来区分「没查到」和「没开」。 */
        sources: Record<string, boolean>
      }>(`/domains/${domainId}/history`),
    staleTime: 60_000,
  })
}

/** ---- 注册商接口调用日志 ---- */

export interface RegistrarCall {
  id: number
  provider: string
  operation: string
  domain?: string
  method: string
  /** 已脱敏：查询串里的密钥会被替换成 ***。 */
  url: string
  http_status: number
  duration_ms: number
  ok: boolean
  error?: string
  response_body?: string
  /** 用户 id，或 'worker' 表示后台自动任务。 */
  actor?: string
  created_at: string
}

export interface RegistrarCallStats {
  total: number
  failed: number
  by_provider: Record<string, number>
  p95_ms: number
}

export interface RegistrarCallFilters {
  provider?: string
  operation?: string
  domain?: string
  failures?: boolean
  window?: string
  limit?: number
}

export function useRegistrarCalls(filters: RegistrarCallFilters, live = true) {
  return useQuery({
    queryKey: ['admin', 'registrar-calls', filters] as const,
    queryFn: () =>
      api.get<{
        calls: RegistrarCall[]
        stats: RegistrarCallStats
        next_id: number
        window: string
        operations: string[]
      }>(`/admin/registrar-calls${qs({ ...filters })}`),
    // Calls land continuously while a drop is being fought over; a log that
    // needs a manual refresh is a log nobody watches during the one minute it
    // matters.
    refetchInterval: live ? 5_000 : false,
  })
}

export interface ProviderTestResult {
  provider: string
  name: string
  /** 哪一套凭据应答的。 */
  account?: string
  ok: boolean
  skipped?: boolean
  error?: string
  available?: boolean
  premium?: boolean
  reason?: string
  price_minor?: number
  currency?: string
  http_status?: number
  elapsed_ms: number
  registered?: boolean
  unknown?: boolean
  charged_minor?: number
  raw?: string
}

export function useTestProviderCheck() {
  return useMutation<{ domain: string; results: ProviderTestResult[] }, ApiError,
    { providers: string[]; domain: string; accounts?: Record<string, string> }>({
    mutationFn: (body) => api.post('/admin/registrar-providers/check', body),
  })
}

export interface ProviderRegisterInput {
  providers: string[]
  domain: string
  /** Must equal `domain`; the server refuses otherwise. */
  confirm_domain: string
  years?: number
  max_price_minor: number
  currency?: string
  skip_precheck?: boolean
  /** 省略时默认为 true：价格上限本身就是授权额度。 */
  allow_premium?: boolean
  /** 每个服务商指定用哪套凭据；留空用第一套。 */
  accounts?: Record<string, string>
}

export function useTestProviderRegister() {
  const qc = useQueryClient()
  return useMutation<{
    domain: string
    order: RegistrationOrder
    results: ProviderTestResult[]
    outcome: 'registered' | 'failed' | 'unknown'
  }, ApiError, ProviderRegisterInput>({
    mutationFn: (body) => api.post('/admin/registrar-providers/register', body),
    // A purchase writes a real order and a real charge; both tables the rest of
    // the app reads from are now stale.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['registration', 'orders'] })
      qc.invalidateQueries({ queryKey: keys.registration.budget })
    },
  })
}

export interface RegistrarAlias {
  alias: string
  source: 'observed' | 'manual'
}

export interface RegistrarPrice {
  tld: string
  currency: string
  register_price?: number
  renew_price?: number
  transfer_price?: number
  note?: string
}

export interface Registrar {
  id: string
  iana_id?: string
  name: string
  short_name?: string
  website?: string
  country?: string
  api_supported: boolean
  drop_catch_ready: boolean
  api_notes?: string
  /** Default currency for new price rows; actual amounts live on prices[]. */
  currency: string
  price_note?: string
  prices?: RegistrarPrice[]
  catch_attempts: number
  catch_successes: number
  catch_rate?: number
  notes?: string
  enabled: boolean
  version: number
  aliases?: RegistrarAlias[]
  domain_count: number
}

export interface RegistrarListParams {
  q?: string
  linked?: boolean
  api_supported?: boolean
  drop_catch_ready?: boolean
}

/** The registrar directory: identity, aliases, capability and pricing. */
export function useRegistrars(params: RegistrarListParams = {}) {
  return useQuery({
    queryKey: [...keys.registrars.all, params] as const,
    queryFn: () =>
      api.get<{ registrars: Registrar[] }>(`/registrars${qs(params as Record<string, unknown>)}`),
    staleTime: 60_000,
  })
}

export type RegistrarInput = Partial<Omit<Registrar,
  'id' | 'version' | 'aliases' | 'prices' | 'domain_count'
  | 'catch_attempts' | 'catch_successes' | 'catch_rate'>>

export function useSetRegistrarPrice() {
  const qc = useQueryClient()
  return useMutation<Registrar, ApiError, { id: string; price: RegistrarPrice }>({
    mutationFn: ({ id, price }) => api.post(`/admin/registrars/${id}/prices`, price),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.registrars.all }),
  })
}

export function useRemoveRegistrarPrice() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, { id: string; tld: string }>({
    mutationFn: ({ id, tld }) =>
      api.delete(`/admin/registrars/${id}/prices?tld=${encodeURIComponent(tld)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.registrars.all }),
  })
}

export function useCreateRegistrar() {
  const qc = useQueryClient()
  return useMutation<Registrar, ApiError, RegistrarInput>({
    mutationFn: (body) => api.post('/admin/registrars', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.registrars.all }),
  })
}

export function useUpdateRegistrar() {
  const qc = useQueryClient()
  return useMutation<Registrar, ApiError, { id: string; body: RegistrarInput }>({
    mutationFn: ({ id, body }) => api.patch(`/admin/registrars/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.registrars.all })
      // Domain rows carry the registrar name, so a rename must reach them too.
      qc.invalidateQueries({ queryKey: keys.domains.all })
    },
  })
}

export function useDeleteRegistrar() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete(`/admin/registrars/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.registrars.all })
      qc.invalidateQueries({ queryKey: keys.domains.all })
    },
  })
}

export function useAddRegistrarAlias() {
  const qc = useQueryClient()
  return useMutation<Registrar, ApiError, { id: string; alias: string }>({
    mutationFn: ({ id, alias }) => api.post(`/admin/registrars/${id}/aliases`, { alias }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.registrars.all }),
  })
}

export function useRemoveRegistrarAlias() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, { id: string; alias: string }>({
    mutationFn: ({ id, alias }) =>
      api.delete(`/admin/registrars/${id}/aliases?alias=${encodeURIComponent(alias)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.registrars.all }),
  })
}

export function useTlds() {
  return useQuery({
    queryKey: keys.meta.tlds,
    queryFn: () => api.get<{ tlds: TldInfo[]; notice: string }>('/meta/tlds'),
    staleTime: 300_000,
  })
}

// ---- Domains ----------------------------------------------------------------

export interface DomainListParams {
  q?: string
  tld?: string[]
  status?: string[]
  charset?: string[]
  feature?: string[]
  /** Operator-defined labels; matched verbatim, never comma-split. */
  tag?: string[]
  source?: string[]
  length_min?: number
  length_max?: number
  watched?: boolean
  favorite?: boolean
  rule_id?: string
  match_status?: string
  resolved_before?: string
  registrar_id?: string[]
  zh_lucky?: boolean
  zh_initial_abbr?: boolean
  zh_syllables?: number
  zh_in_dictionary?: boolean
  expires_from?: string
  expires_to?: string
  drop_from?: string
  drop_to?: string
  last_seen_from?: string
  last_seen_to?: string
  sort?: string
  /** Overrides the sort key's natural direction. */
  order?: 'asc' | 'desc' | string
  limit?: number
  cursor?: string
}

export function useDomains(params: DomainListParams) {
  return useQuery<Page<Domain>, ApiError>({
    queryKey: keys.domains.list(params as Record<string, unknown>),
    queryFn: () => api.get<Page<Domain>>(`/domains${qs(params as Record<string, unknown>)}`),
    // Keeping the previous page visible while the next loads avoids a full
    // table flash on every filter tweak.
    placeholderData: (prev) => prev,
    staleTime: 15_000,
    retry: shouldRetry,
  })
}

export function useFavoriteDomain() {
  const qc = useQueryClient()
  return useMutation<{ domain_id: string; favorite: boolean }, ApiError, string>({
    mutationFn: (domainId) => api.post(`/domains/${domainId}/favorite`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.domains.all }),
  })
}

export function useUnfavoriteDomain() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (domainId) => api.delete(`/domains/${domainId}/favorite`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.domains.all }),
  })
}

export interface AdminDomainInput {
  domain?: string
  status: string
  expires_at: string | null
  current_registrar: string
}

export function useAdminCreateDomain() {
  const qc = useQueryClient()
  return useMutation<{ id: string; domain: string }, ApiError, AdminDomainInput>({
    mutationFn: (body) => api.post('/admin/domains', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.domains.all }),
  })
}

export interface AdminLookupResult {
  domain: string
  found: boolean
  status?: string
  registrar?: string
  expires_at?: string
  confidence?: number
  raw_statuses?: string[]
  nameservers?: string[]
  error?: string
  notice?: string
}

/**
 * Ask the registry what a name looks like, so the create form fills itself in.
 * Nothing is written: the domain has no row yet, and a lookup the user then
 * cancels should leave no trace.
 */
export function useAdminLookupDomain() {
  return useMutation<AdminLookupResult, ApiError, string>({
    mutationFn: (domain) => api.post('/admin/domains/lookup', { domain }),
    retry: false,
  })
}

export function useAdminUpdateDomain() {
  const qc = useQueryClient()
  return useMutation<unknown, ApiError, { id: string; body: AdminDomainInput }>({
    mutationFn: ({ id, body }) => api.patch(`/admin/domains/${id}`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: keys.domains.all })
      qc.invalidateQueries({ queryKey: keys.domains.detail(vars.id) })
    },
  })
}

export function useAdminDeleteDomain() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, { id: string; confirm_domain: string }>({
    mutationFn: ({ id, confirm_domain }) =>
      api.delete(`/admin/domains/${id}`, { confirm_domain }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.domains.all }),
  })
}

export interface BulkDeleteResult {
  result: { domains: number; events: number; watchers: number; rule_matches: number }
  notice: string
}

/**
 * confirm_count must equal the number of ids. The mistake it guards against is
 * a selection carried across pages or left over from a filter that has since
 * changed — a checkbox cannot catch that, a number the operator has to read and
 * retype can.
 */
export function useAdminBulkDeleteDomains() {
  const qc = useQueryClient()
  return useMutation<BulkDeleteResult, ApiError, string[]>({
    mutationFn: (ids) =>
      api.post('/admin/domains/bulk-delete', { ids, confirm_count: ids.length }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.domains.all }),
    retry: false,
  })
}

export function useDomain(id: string | null) {
  return useQuery<DomainDetail, ApiError>({
    queryKey: keys.domains.detail(id ?? ''),
    queryFn: () => api.get<DomainDetail>(`/domains/${id}`),
    enabled: Boolean(id),
    retry: shouldRetry,
  })
}

export function useDomainEvents(id: string | null) {
  return useQuery<Page<DomainEvent>, ApiError>({
    queryKey: keys.domains.events(id ?? ''),
    queryFn: () => api.get<Page<DomainEvent>>(`/domains/${id}/events?limit=100`),
    enabled: Boolean(id),
    retry: shouldRetry,
  })
}

export function useDomainScore(id: string | null) {
  return useQuery({
    queryKey: keys.domains.score(id ?? ''),
    queryFn: () =>
      api.get<{
        score_version: string
        score: number
        components: Array<{
          key: string; value: number; weight: number; contribution: number
          available: boolean; evidence?: string[]
        }>
        confidence: number
        computed_at: string
        disclaimer: string
      }>(`/domains/${id}/score`),
    enabled: Boolean(id),
    // A missing score is normal for a brand-new domain; do not retry it.
    retry: false,
  })
}

export function useRegistrarOffer(id: string | null) {
  return useQuery<AffiliateOffer, ApiError>({
    queryKey: keys.domains.registrars(id ?? ''),
    queryFn: () => api.get<AffiliateOffer>(`/domains/${id}/registrars`),
    enabled: Boolean(id),
    retry: false,
  })
}

export function useResolveDomain() {
  const qc = useQueryClient()
  return useMutation<{ job_id: string; status: string; message: string }, ApiError, string>({
    mutationFn: (domainId) =>
      api.post(`/domains/${domainId}/resolve`, {}, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: (_data, domainId) => {
      qc.invalidateQueries({ queryKey: keys.domains.detail(domainId) })
      qc.invalidateQueries({ queryKey: keys.usage })
    },
  })
}

export interface QueryDomainsResult {
  results: Array<{
    input: string
    domain?: string
    domain_unicode?: string
    known: boolean
    record?: Domain
    features?: string[]
    status_fresh: boolean
    confirmation_queued: boolean
    error?: string
  }>
  created: number
  confirmations_queued: number
  notice: string
}

export function useQueryDomains() {
  const qc = useQueryClient()
  return useMutation<QueryDomainsResult, ApiError, { domains: string[]; resolve: boolean }>({
    mutationFn: (body) => api.post<QueryDomainsResult>('/domains/query', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.domains.all })
      qc.invalidateQueries({ queryKey: keys.usage })
    },
  })
}

// ---- Rules ------------------------------------------------------------------

export function useRules(status?: string) {
  return useQuery<Page<Rule>, ApiError>({
    queryKey: keys.rules.list(status),
    queryFn: () => api.get<Page<Rule>>(`/rules${qs({ status, limit: 100 })}`),
    retry: shouldRetry,
  })
}

export function useRule(id: string | null) {
  return useQuery<Rule, ApiError>({
    queryKey: keys.rules.detail(id ?? ''),
    queryFn: () => api.get<Rule>(`/rules/${id}`),
    enabled: Boolean(id),
    retry: shouldRetry,
  })
}

export function useValidateRule() {
  return useMutation<RuleValidation, ApiError, { rule_json: RuleDocument; name?: string }>({
    mutationFn: (body) => api.post<RuleValidation>('/rules/validate', body),
    // Validation is a user-triggered check; a failure should surface at once.
    retry: false,
  })
}

export function useCreateRule() {
  const qc = useQueryClient()
  return useMutation<
    { rule: Rule; estimate: unknown },
    ApiError,
    { rule_json: RuleDocument; name: string; description?: string; status?: string }
  >({
    mutationFn: (body) => api.post('/rules', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.rules.all }),
  })
}

export function useUpdateRule() {
  const qc = useQueryClient()
  return useMutation<
    { rule: Rule; estimate: unknown },
    ApiError,
    { id: string; version: number; body: Record<string, unknown> }
  >({
    // The version travels as If-Match so a concurrent edit is a 409 rather
    // than a silent overwrite (§10.1).
    mutationFn: ({ id, version, body }) => api.patch(`/rules/${id}`, body, { version }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: keys.rules.all })
      qc.invalidateQueries({ queryKey: keys.rules.detail(vars.id) })
    },
  })
}

export interface RuleDeleteResult {
  deleted: boolean
  purged?: { domains: number; kept: number; events: number }
  notice?: string
}

/**
 * purgeDomains is opt-in and defaults off: deleting a rule is routine, deleting
 * shared domains is not reversible, and the two should not happen together by
 * accident.
 */
export function useDeleteRule() {
  const qc = useQueryClient()
  return useMutation<RuleDeleteResult, ApiError, { id: string; purgeDomains?: boolean }>({
    mutationFn: ({ id, purgeDomains }) =>
      api.delete(`/rules/${id}${purgeDomains ? '?purge_domains=true' : ''}`),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: keys.rules.all })
      // The library and everything derived from it may have lost rows.
      if (vars.purgeDomains) qc.invalidateQueries({ queryKey: keys.domains.all })
    },
  })
}

export interface RuleVersion {
  version: number
  rule_json: RuleDocument
  name: string
  description: string
  at: string
  current: boolean
}

/**
 * The bodies behind the version numbers stamped on runs and matches. Without
 * these a hit labelled "v1" refers to a rule that no longer exists anywhere.
 */
export function useRuleVersions(ruleId: string | null) {
  return useQuery<{ items: RuleVersion[]; notice: string }, ApiError>({
    queryKey: ['rules', 'versions', ruleId ?? ''],
    queryFn: () => api.get(`/rules/${ruleId}/versions`),
    enabled: !!ruleId,
    retry: shouldRetry,
  })
}

export function useTestRule(ruleId: string | null) {
  return useMutation<
    { results: TestResult[]; engine_version: string; notice: string },
    ApiError,
    { samples: string[]; use_library_sample?: boolean; rule_json?: RuleDocument }
  >({
    mutationFn: (body) => api.post(body.rule_json ? '/rules/test' : `/rules/${ruleId}/test`, body),
    retry: false,
  })
}

export function useStartRun() {
  const qc = useQueryClient()
  return useMutation<{ run_id: string; job_id: string; status: string }, ApiError, string>({
    mutationFn: (ruleId) =>
      api.post(`/rules/${ruleId}/runs`, {}, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: (_data, ruleId) => {
      qc.invalidateQueries({ queryKey: keys.rules.runs(ruleId) })
      qc.invalidateQueries({ queryKey: keys.rules.all })
    },
  })
}

export function useRuleRuns(ruleId: string | null, poll = false) {
  return useQuery<Page<RuleRun>, ApiError>({
    queryKey: keys.rules.runs(ruleId ?? ''),
    queryFn: () => api.get<Page<RuleRun>>(`/rules/${ruleId}/runs?limit=20`),
    enabled: Boolean(ruleId),
    // MVP uses polling rather than SSE (§10.1), and only while a run is live.
    refetchInterval: poll ? 4000 : false,
    retry: shouldRetry,
  })
}

export function useRuleMatches(ruleId: string | null, matchStatus: string) {
  return useQuery<{ items: RuleMatch[]; counts: Record<string, number> }, ApiError>({
    queryKey: keys.rules.matches(ruleId ?? '', matchStatus),
    queryFn: () =>
      api.get(`/rules/${ruleId}/matches${qs({ match_status: matchStatus, limit: 100 })}`),
    enabled: Boolean(ruleId),
    retry: shouldRetry,
  })
}

// ---- Watchers ---------------------------------------------------------------

export function useWatchers(status?: string) {
  return useQuery<
    {
      items: Watcher[]
      /** Per-status totals, counted server-side so the page limit cannot skew them. */
      counts: Record<string, number>
      limits: { max_active_watchers: number; notification_delay_seconds: number }
    },
    ApiError
  >({
    queryKey: keys.watchers(status),
    queryFn: () => api.get(`/watchers${qs({ status, limit: 200 })}`),
    retry: shouldRetry,
  })
}

export function useCreateWatcher() {
  const qc = useQueryClient()
  return useMutation<
    { id: string; domain: string; notification_delay_seconds: number; notice?: string },
    ApiError,
    { domain_id?: string; domain?: string; priority?: string; note?: string }
  >({
    mutationFn: (body) => api.post('/watchers', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['watchers'] })
      qc.invalidateQueries({ queryKey: keys.domains.all })
      qc.invalidateQueries({ queryKey: keys.me })
    },
  })
}

export function useUpdateWatcher() {
  const qc = useQueryClient()
  return useMutation<unknown, ApiError, { id: string; body: Record<string, unknown> }>({
    mutationFn: ({ id, body }) => api.patch(`/watchers/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchers'] }),
  })
}

export function useDeleteWatcher() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete(`/watchers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['watchers'] })
      qc.invalidateQueries({ queryKey: keys.domains.all })
    },
  })
}

// ---- Events -----------------------------------------------------------------

export function useEventFeed(params: { event_type?: string; since?: string; limit?: number }) {
  return useQuery<Page<DomainEvent>, ApiError>({
    queryKey: keys.events(params),
    queryFn: () => api.get<Page<DomainEvent>>(`/events${qs(params)}`),
    retry: shouldRetry,
  })
}

export function useReleaseAnalytics(params: {
  tld?: string
  registrar?: string
  from?: string
  to?: string
  scope?: 'demo' | 'observed'
  limit?: number
}) {
  return useQuery<ReleaseAnalytics, ApiError>({
    queryKey: keys.releaseAnalytics(params),
    queryFn: () => api.get<ReleaseAnalytics>(`/release-analytics${qs(params)}`),
    staleTime: 30_000,
    retry: shouldRetry,
  })
}

// ---- Notifications ----------------------------------------------------------

export function useChannels() {
  return useQuery<Page<NotificationChannel>, ApiError>({
    queryKey: keys.channels,
    queryFn: () => api.get<Page<NotificationChannel>>('/notification-channels'),
    retry: shouldRetry,
  })
}

export function useCreateChannel() {
  const qc = useQueryClient()
  return useMutation<
    { id: string; status: string; notice: string },
    ApiError,
    { name: string; kind: string; config: Record<string, unknown>; secret?: string }
  >({
    mutationFn: (body) => api.post('/notification-channels', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.channels }),
  })
}

export function useDeleteChannel() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete(`/notification-channels/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.channels }),
  })
}

export function useTestChannel() {
  const qc = useQueryClient()
  return useMutation<{ verified: boolean; error?: string; notice?: string }, ApiError, string>({
    mutationFn: (id) => api.post(`/notification-channels/${id}/test`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.channels }),
  })
}

export function useDeliveries(params: { status?: string; channel_kind?: string; limit?: number }) {
  return useQuery<{ items: Delivery[]; unread: number }, ApiError>({
    queryKey: keys.deliveries(params),
    queryFn: () => api.get(`/notification-deliveries${qs(params)}`),
    refetchInterval: 30_000,
    retry: shouldRetry,
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.post(`/notification-deliveries/${id}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliveries'] }),
  })
}

// ---- Dashboard --------------------------------------------------------------

export function useDashboard(days: number) {
  return useQuery<DashboardSummary, ApiError>({
    queryKey: keys.dashboard(days),
    queryFn: () => api.get<DashboardSummary>(`/dashboard/summary${qs({ days })}`),
    staleTime: 30_000,
    retry: shouldRetry,
  })
}

// ---- Admin ------------------------------------------------------------------

export function useAdminHealth(enabled: boolean) {
  return useQuery<AdminHealth, ApiError>({
    queryKey: keys.admin.health,
    queryFn: () => api.get<AdminHealth>('/admin/health'),
    enabled,
    refetchInterval: enabled ? 15_000 : false,
    retry: shouldRetry,
  })
}

export function useDeadLetters(queue: string, enabled: boolean) {
  return useQuery<Page<DeadLetter>, ApiError>({
    queryKey: keys.admin.deadLetters(queue),
    queryFn: () => api.get<Page<DeadLetter>>(`/admin/dead-letters${qs({ queue, limit: 50 })}`),
    enabled,
    retry: shouldRetry,
  })
}

export interface PurgeScope {
  source?: string
  tld?: string
  min_label_length?: number
  max_label_length?: number
  include_watched?: boolean
  include_matched?: boolean
}

export interface PurgeCounts {
  dry_run: boolean
  domains: number
  domain_events: number
  status_observations: number
  rule_matches: number
  watchers: number
  score_snapshots: number
  usage_events_preserved: number
  duration?: string
}

/**
 * Previews a purge. The UI always runs this before offering to delete, so the
 * blast radius is on screen before anyone confirms.
 */
export function usePurgePreview() {
  return useMutation<{ preview: PurgeCounts; notice: string }, ApiError, PurgeScope>({
    mutationFn: (scope) => api.post('/admin/domains/purge', { ...scope, dry_run: true }),
    retry: false,
  })
}

export function usePurgeDomains() {
  const qc = useQueryClient()
  return useMutation<{ result: PurgeCounts; notice: string }, ApiError, PurgeScope & { confirm: string }>({
    mutationFn: (body) => api.post('/admin/domains/purge', body),
    onSuccess: () => {
      // Everything derived from the library is now stale.
      qc.clear()
    },
    retry: false,
  })
}

// ---- Lookup history ---------------------------------------------------------

/**
 * Every external lookup lands here, refusals included. A registry saying 429 or
 * 403 is recorded as a row like any other, which is what makes throttling
 * visible: without it the only symptom is that the domain quietly stops being
 * confirmed as often as the schedule claims.
 */
export interface DomainObservations {
  domain: string
  summary: {
    total: number
    ok: number
    throttled: number
    failed: number
    first_at?: string
    last_at?: string
    mean_latency_ms?: number
    /** Typical spacing between lookups — compare with the configured cadence. */
    median_interval_seconds?: number
  }
  /** Cursor for the next page; absent once the history is exhausted. */
  next_before?: string
  items: Array<{
    id: string
    source: string
    queried_at: string
    normalized_status: string
    outcome: 'ok' | 'throttled' | 'failed'
    http_status?: number
    latency_ms?: number
    confidence: number
    registrar?: string
    error_class?: string
    error_message?: string
    raw_statuses?: string[]
  }>
}

/**
 * Paged by timestamp rather than page number: a watched domain adds a row every
 * few seconds, so an offset taken now points somewhere else by the time the
 * next page is requested, silently skipping or repeating rows.
 */
export function useDomainObservations(
  domainId: string | null,
  filters: { source?: string; outcome?: string; limit?: number },
) {
  return useInfiniteQuery<DomainObservations, ApiError>({
    queryKey: ['domains', 'observations', domainId ?? '', filters],
    queryFn: ({ pageParam }) =>
      api.get<DomainObservations>(
        `/domains/${domainId}/observations${qs({ ...filters, before: pageParam as string | undefined })}`,
      ),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_before,
    enabled: !!domainId,
    staleTime: 5_000,
    retry: shouldRetry,
  })
}

// ---- Rate-limit calibration -------------------------------------------------

/**
 * The configured qps is a number this project chose, not one any registry
 * published, so it stays an untested guess until a 429 appears. These hooks
 * back the calibration tab that turns that into a measurement.
 */
export interface ResolverLimits {
  window_seconds: number
  /** Committed pressure: queued work fires regardless of what is decided next. */
  resolve_backlog: number
  max_backlog_forload: number
  max_rejection_rate: number
  cooldown_minutes: number
  rdap_hosts: Array<{
    host: string
    tlds: string[]
    configured_qps: number
    configured_burst: number
    attempts: number
    deferred_by_us: number
    rejected_upstream: number
    mean_qps: number
    circuit_breaker: string
    verdict: 'idle' | 'headroom' | 'self_limited' | 'at_upstream_limit' | 'breaker_open'
    advice: string
    source: 'override' | 'config'
    note?: string
    rejection_rate: number
    cooling_until?: string
  }>
  whois_servers: Array<{
    server: string
    configured_qps: number
    configured_burst: number
    attempts: number
    deferred_by_us: number
  }>
  whois_note: string
  how_to_calibrate: string[]
  note: string
}

export function useResolverLimits(enabled: boolean, poll: boolean) {
  return useQuery<ResolverLimits, ApiError>({
    queryKey: keys.admin.resolverLimits,
    queryFn: () => api.get<ResolverLimits>('/admin/resolver/limits'),
    enabled,
    // Polling only while an experiment is running: the counters are a rolling
    // window and move slowly, so refetching constantly buys nothing.
    refetchInterval: enabled && poll ? 15_000 : false,
    retry: shouldRetry,
  })
}

export function useResetResolverLimits() {
  const qc = useQueryClient()
  return useMutation<{ scopes_cleared: number; note: string }, ApiError, void>({
    mutationFn: () => api.post('/admin/resolver/limits/reset', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admin.resolverLimits }),
    retry: false,
  })
}

/**
 * Writing a budget takes effect on the next lookup, not the next deploy, so
 * invalidating the limits query is enough — there is nothing to restart.
 */
export function useSetResolverLimit() {
  const qc = useQueryClient()
  return useMutation<
    { host: string; note: string },
    ApiError,
    { host: string; qps?: number; burst?: number; note?: string; clear?: boolean }
  >({
    mutationFn: (body) => api.patch('/admin/resolver/limits', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admin.resolverLimits }),
    retry: false,
  })
}

export function useGenerateCalibrationLoad() {
  const qc = useQueryClient()
  return useMutation<
    { tld: string; marked: number; note: string },
    ApiError,
    { tld: string; count: number }
  >({
    mutationFn: (body) => api.post('/admin/resolver/limits/load', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admin.resolverLimits }),
    retry: false,
  })
}

export function useReplayDeadLetter() {
  const qc = useQueryClient()
  return useMutation<{ new_job_id: string }, ApiError, string>({
    mutationFn: (jobId) => api.post(`/admin/dead-letters/${jobId}/replay`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] })
    },
  })
}
